
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

export interface UserCouponCredit {
  id: string;
  user_coupon_id: string;
  period_start: string;
  period_end: string;
  credits_used: number;
  credits_available: number;
  created_at: string;
  updated_at: string;
}

// Helper function to get Monday of the week for a given date (ISO standard)
const getMondayOfWeek = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  return new Date(d.setDate(diff));
};

export const useUserCouponCredits = (userCouponId?: string, bookingDate?: Date) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['user-coupon-credits', userCouponId, user?.id, bookingDate?.toISOString()],
    queryFn: async () => {
      if (!user || !userCouponId) return null;

      console.log('useUserCouponCredits - Fetching credits for userCouponId:', userCouponId);
      console.log('useUserCouponCredits - With booking date:', bookingDate?.toISOString());

      // If we have a booking date, get credits for that specific date's period
      if (bookingDate) {
        // First get the coupon info to determine period type and credits per period
        const { data: userCoupon, error: userCouponError } = await supabase
          .from('user_coupons')
          .select(`
            *,
            coupon:coupons(
              discount_type,
              credits_per_period
            )
          `)
          .eq('id', userCouponId)
          .single();

        if (userCouponError) {
          console.error('Error fetching user coupon:', userCouponError);
          throw userCouponError;
        }

        const coupon = userCoupon.coupon;
        if (coupon && (coupon.discount_type === 'weekly_credits' || coupon.discount_type === 'monthly_credits')) {
          const periodType = coupon.discount_type === 'weekly_credits' ? 'weekly' : 'monthly';
          const creditsPerPeriod = coupon.credits_per_period || 1;
          const bookingDateString = bookingDate.toISOString().split('T')[0];

          console.log('useUserCouponCredits - Getting period credits for date:', bookingDateString);
          console.log('useUserCouponCredits - Period type:', periodType);

          // For weekly credits, let's also log the Monday calculation for debugging
          if (periodType === 'weekly') {
            const mondayOfWeek = getMondayOfWeek(bookingDate);
            console.log('useUserCouponCredits - Frontend calculated Monday for week:', mondayOfWeek.toISOString().split('T')[0]);
          }

          // Get or create period credits for the specific booking date
          const { data: periodCredits, error: periodError } = await supabase.rpc('get_or_create_period_credits', {
            p_user_coupon_id: userCouponId,
            p_credits_per_period: creditsPerPeriod,
            p_period_type: periodType,
            p_booking_date: bookingDateString
          });

          if (periodError) {
            console.error('Error getting period credits:', periodError);
            throw periodError;
          }

          console.log('useUserCouponCredits - Period credits result:', periodCredits);
          return periodCredits as UserCouponCredit | null;
        }
      }

      // Fallback to current period logic for non-credit coupons or when no booking date
      const { data: allCredits, error: allCreditsError } = await supabase
        .from('user_coupon_credits')
        .select('*')
        .eq('user_coupon_id', userCouponId)
        .order('period_start', { ascending: false });

      if (allCreditsError) {
        console.error('Error fetching all user coupon credits:', allCreditsError);
      } else {
        console.log('useUserCouponCredits - All credit periods:', allCredits);
      }

      // Get the current period's credits
      const { data, error } = await supabase
        .from('user_coupon_credits')
        .select('*')
        .eq('user_coupon_id', userCouponId)
        .order('period_start', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user coupon credits:', error);
        throw error;
      }

      console.log('useUserCouponCredits - Current period credits:', data);

      return data as UserCouponCredit | null;
    },
    enabled: !!user && !!userCouponId,
  });
};

export const useGetOrCreatePeriodCredits = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ 
      userCouponId, 
      creditsPerPeriod, 
      periodType,
      bookingDate 
    }: {
      userCouponId: string;
      creditsPerPeriod: number;
      periodType: 'weekly' | 'monthly';
      bookingDate?: Date;
    }) => {
      console.log('useGetOrCreatePeriodCredits - Input params:', {
        userCouponId,
        creditsPerPeriod,
        periodType,
        bookingDate: bookingDate?.toISOString()
      });

      // Format the booking date for the database function
      const bookingDateString = bookingDate ? bookingDate.toISOString().split('T')[0] : undefined;

      // Call the RPC function with the correct parameter order
      const { data, error } = await supabase.rpc('get_or_create_period_credits', {
        p_user_coupon_id: userCouponId,
        p_credits_per_period: creditsPerPeriod,
        p_period_type: periodType,
        ...(bookingDateString && { p_booking_date: bookingDateString })
      });

      if (error) {
        console.error('Error getting/creating period credits:', error);
        throw error;
      }

      console.log('useGetOrCreatePeriodCredits - Result:', data);

      return data as UserCouponCredit;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-coupon-credits'] });
    },
    onError: (error: any) => {
      console.error('Error with period credits:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to manage coupon credits. Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useUseCouponCredit = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ userCouponCreditId }: { userCouponCreditId: string }) => {
      console.log('useUseCouponCredit - Using credit ID:', userCouponCreditId);

      // First get the current credits_used value
      const { data: currentCredit, error: fetchError } = await supabase
        .from('user_coupon_credits')
        .select('credits_used, credits_available')
        .eq('id', userCouponCreditId)
        .single();

      if (fetchError) {
        console.error('Error fetching current credit:', fetchError);
        throw fetchError;
      }

      console.log('useUseCouponCredit - Current credit state:', currentCredit);

      // Then update with the incremented value
      const { data, error } = await supabase
        .from('user_coupon_credits')
        .update({ 
          credits_used: currentCredit.credits_used + 1,
          updated_at: new Date().toISOString()
        })
        .eq('id', userCouponCreditId)
        .select()
        .single();

      if (error) {
        console.error('Error using coupon credit:', error);
        throw error;
      }

      console.log('useUseCouponCredit - Updated credit state:', data);

      return data as UserCouponCredit;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-coupon-credits'] });
      // toast({
      //   title: "Credit Used",
      //   description: "Your coupon credit has been applied to the booking.",
      // });
    },
    onError: (error: any) => {
      console.error('Error using coupon credit:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to use coupon credit. Please try again.",
        variant: "destructive",
      });
    },
  });
};
